import type {
  AssistantActivity,
  AssistantState,
  AssistantToolCallState,
} from '@/embedded/chat/types/runtime-state';
import type {
  ConversationMessage,
  ConversationMessageContent,
} from '@/embedded/chat/types/conversation-message';
import {
  expandQuoteAwareText,
  normalizeQuoteReference,
} from '@/utils/chat-quote';
import { createPrefixedRandomID } from '@/utils/random-id';
import { shouldRetainAssistantActivityForSurface } from './conversation-core/activity-retention';
import { appRequestContentFromMessageParts } from './conversation-core/conversation-message-thread';
import type { WebAgentStoredMessage } from './chat-api';

type TrackedToolCall = {
  id: string;
  name: string;
  kind: 'web_browse' | 'interactive_login' | 'ignored';
};

export type WebChatAdapterOutput = {
  conversationMessages: ConversationMessage[];
  assistantState?: AssistantState | null;
  assistantActivity?: AssistantActivity | null;
  sessionStarted?: boolean;
  sessionFinished?: { state: string; error?: string };
  titleChanged?: string;
};

const nowInSeconds = () => Date.now() / 1000;

function parseJSON(raw: string | undefined | null): unknown {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function makeTextContent(text: string) {
  return {
    type: 'text',
    text,
  };
}

type MessageContentInput = unknown;

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === 'object' && raw !== null;
}

function normalizedString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
}

function normalizedNumber(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

function normalizedBoolean(raw: unknown): boolean | undefined {
  return typeof raw === 'boolean' ? raw : undefined;
}

function normalizedStringArray(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const values = raw
    .map(item => normalizedString(item))
    .filter((item): item is string => !!item);
  return values.length > 0 ? values : undefined;
}

function normalizeHostAccess(
  raw: unknown
): ConversationMessageContent['hostAccess'] | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  const hostAccess = {
    environmentId: normalizedString(raw.environmentId ?? raw.environment_id),
    environmentLabel: normalizedString(
      raw.environmentLabel ?? raw.environment_label
    ),
    description: normalizedString(raw.description),
    status: normalizedString(raw.status),
    approved: typeof raw.approved === 'boolean' ? raw.approved : undefined,
  };
  return Object.values(hostAccess).some(value => value !== undefined)
    ? hostAccess
    : undefined;
}

function normalizeComputerUseStart(
  raw: unknown
): ConversationMessageContent['computerUseStart'] | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  const computerUseStart = {
    environmentId: normalizedString(raw.environmentId ?? raw.environment_id),
    environmentLabel: normalizedString(
      raw.environmentLabel ?? raw.environment_label
    ),
    availableModes: normalizedStringArray(
      raw.availableModes ?? raw.available_modes
    ),
    apps: normalizedStringArray(raw.apps),
    status: normalizedString(raw.status),
    approved: typeof raw.approved === 'boolean' ? raw.approved : undefined,
    mode: normalizedString(raw.mode),
  };
  return Object.values(computerUseStart).some(value => value !== undefined)
    ? computerUseStart
    : undefined;
}

function normalizeFileRef(
  raw: unknown
): ConversationMessageContent['fileRef'] | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }

  const path =
    'path' in raw && typeof raw.path === 'string'
      ? normalizedString(raw.path)
      : undefined;
  const refId =
    'refId' in raw && typeof raw.refId === 'string'
      ? normalizedString(raw.refId)
      : 'ref_id' in raw && typeof raw.ref_id === 'string'
        ? normalizedString(raw.ref_id)
        : undefined;
  const displayURL =
    'displayURL' in raw && typeof raw.displayURL === 'string'
      ? normalizedString(raw.displayURL)
      : 'displayUrl' in raw && typeof raw.displayUrl === 'string'
        ? normalizedString(raw.displayUrl)
        : 'display_url' in raw && typeof raw.display_url === 'string'
          ? normalizedString(raw.display_url)
          : undefined;
  const downloadURL =
    'downloadURL' in raw && typeof raw.downloadURL === 'string'
      ? normalizedString(raw.downloadURL)
      : 'downloadUrl' in raw && typeof raw.downloadUrl === 'string'
        ? normalizedString(raw.downloadUrl)
        : 'download_url' in raw && typeof raw.download_url === 'string'
          ? normalizedString(raw.download_url)
          : undefined;
  const thumbnailURL =
    'thumbnailURL' in raw && typeof raw.thumbnailURL === 'string'
      ? normalizedString(raw.thumbnailURL)
      : 'thumbnailUrl' in raw && typeof raw.thumbnailUrl === 'string'
        ? normalizedString(raw.thumbnailUrl)
        : 'thumbnail_url' in raw && typeof raw.thumbnail_url === 'string'
          ? normalizedString(raw.thumbnail_url)
          : undefined;
  const publicShareURL =
    'publicShareURL' in raw && typeof raw.publicShareURL === 'string'
      ? normalizedString(raw.publicShareURL)
      : 'publicShareUrl' in raw && typeof raw.publicShareUrl === 'string'
        ? normalizedString(raw.publicShareUrl)
        : 'public_share_url' in raw && typeof raw.public_share_url === 'string'
          ? normalizedString(raw.public_share_url)
          : undefined;
  if (!path && !refId && !displayURL && !downloadURL && !publicShareURL) {
    return undefined;
  }

  const environmentId =
    'environmentId' in raw && typeof raw.environmentId === 'string'
      ? raw.environmentId
      : 'environment_id' in raw && typeof raw.environment_id === 'string'
        ? raw.environment_id
        : undefined;
  const sourceSessionId =
    'sourceSessionId' in raw && typeof raw.sourceSessionId === 'string'
      ? raw.sourceSessionId
      : 'source_session_id' in raw && typeof raw.source_session_id === 'string'
        ? raw.source_session_id
        : undefined;
  const agentId =
    'agentId' in raw && typeof raw.agentId === 'string'
      ? raw.agentId
      : 'agent_id' in raw && typeof raw.agent_id === 'string'
        ? raw.agent_id
        : undefined;
  const signature =
    'signature' in raw && typeof raw.signature === 'string'
      ? raw.signature
      : undefined;
  const expiresAt =
    'expiresAt' in raw && typeof raw.expiresAt === 'number'
      ? raw.expiresAt
      : 'expires_at' in raw && typeof raw.expires_at === 'number'
        ? raw.expires_at
        : undefined;

  return {
    refId,
    kind: 'kind' in raw ? normalizedString(raw.kind) : undefined,
    source: 'source' in raw ? normalizedString(raw.source) : undefined,
    path,
    name: 'name' in raw ? normalizedString(raw.name) : undefined,
    mimeType:
      'mimeType' in raw
        ? normalizedString(raw.mimeType)
        : 'mime_type' in raw
          ? normalizedString(raw.mime_type)
          : undefined,
    sizeBytes:
      'sizeBytes' in raw
        ? normalizedNumber(raw.sizeBytes)
        : 'size_bytes' in raw
          ? normalizedNumber(raw.size_bytes)
          : undefined,
    contentHash:
      'contentHash' in raw
        ? normalizedString(raw.contentHash)
        : 'content_hash' in raw
          ? normalizedString(raw.content_hash)
          : undefined,
    displayURL,
    downloadURL,
    thumbnailURL,
    publicShareURL,
    authRequired:
      'authRequired' in raw
        ? normalizedBoolean(raw.authRequired)
        : 'auth_required' in raw
          ? normalizedBoolean(raw.auth_required)
          : undefined,
    authScope:
      'authScope' in raw
        ? normalizedString(raw.authScope)
        : 'auth_scope' in raw
          ? normalizedString(raw.auth_scope)
          : undefined,
    environmentId,
    sourceSessionId,
    agentId,
    signature,
    expiresAt,
  };
}

function normalizeFileRefs(
  raw: unknown
): ConversationMessageContent['fileRefs'] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }

  const refs = raw.flatMap(item => {
    const normalized = normalizeFileRef(item);
    return normalized ? [normalized] : [];
  });
  return refs.length > 0 ? refs : undefined;
}

function isVFSFileRef(
  fileRef: ConversationMessageContent['fileRef']
): fileRef is NonNullable<ConversationMessageContent['fileRef']> {
  if (!fileRef) {
    return false;
  }

  const environmentId = fileRef.environmentId?.trim().toLowerCase() ?? '';
  return environmentId === 'vfs' || environmentId === '';
}

function selectPrimaryFileRef(params: {
  fileRef?: ConversationMessageContent['fileRef'];
  fileRefs?: ConversationMessageContent['fileRefs'];
}) {
  if (params.fileRef) {
    return params.fileRef;
  }

  return params.fileRefs?.find(isVFSFileRef) ?? params.fileRefs?.[0];
}

function normalizeContentBlock(
  raw: unknown
): ConversationMessageContent | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }

  const type = 'type' in raw && typeof raw.type === 'string' ? raw.type : null;
  if (!type) {
    if ('text' in raw && typeof raw.text === 'string') {
      return makeTextContent(raw.text);
    }
    return undefined;
  }

  const content: ConversationMessageContent = {
    type,
  };

  if ('text' in raw && typeof raw.text === 'string') {
    content.text = raw.text;
  }
  if ('title' in raw && typeof raw.title === 'string') {
    content.title = raw.title;
  }
  if ('url' in raw && typeof raw.url === 'string') {
    content.url = raw.url;
  }

  const fileRef = normalizeFileRef(
    'fileRef' in raw ? raw.fileRef : 'file_ref' in raw ? raw.file_ref : null
  );
  const fileRefs = normalizeFileRefs(
    'fileRefs' in raw ? raw.fileRefs : 'file_refs' in raw ? raw.file_refs : null
  );
  if (fileRefs) {
    content.fileRefs = fileRefs;
  }

  const primaryFileRef = selectPrimaryFileRef({ fileRef, fileRefs });
  if (primaryFileRef) {
    content.fileRef = primaryFileRef;
  }

  const fileName =
    'fileName' in raw && typeof raw.fileName === 'string'
      ? raw.fileName
      : 'file_name' in raw && typeof raw.file_name === 'string'
        ? raw.file_name
        : undefined;
  if (fileName) {
    content.fileName = fileName;
  }

  const mimeType =
    'mimeType' in raw && typeof raw.mimeType === 'string'
      ? raw.mimeType
      : 'mime_type' in raw && typeof raw.mime_type === 'string'
        ? raw.mime_type
        : undefined;
  if (mimeType) {
    content.mimeType = mimeType;
  }

  const generationPrompt =
    'generationPrompt' in raw && typeof raw.generationPrompt === 'string'
      ? raw.generationPrompt
      : 'generation_prompt' in raw && typeof raw.generation_prompt === 'string'
        ? raw.generation_prompt
        : undefined;
  if (generationPrompt) {
    content.generationPrompt = generationPrompt;
  }

  const sizeBytes =
    'sizeBytes' in raw && typeof raw.sizeBytes === 'number'
      ? raw.sizeBytes
      : 'size_bytes' in raw && typeof raw.size_bytes === 'number'
        ? raw.size_bytes
        : undefined;
  if (sizeBytes !== undefined) {
    content.sizeBytes = sizeBytes;
  }

  const entryKind =
    'entryKind' in raw && typeof raw.entryKind === 'string'
      ? raw.entryKind
      : 'entry_kind' in raw && typeof raw.entry_kind === 'string'
        ? raw.entry_kind
        : undefined;
  if (entryKind) {
    content.entryKind = entryKind;
  }

  const quoteRef = normalizeQuoteReference(
    'quoteRef' in raw ? raw.quoteRef : 'quote_ref' in raw ? raw.quote_ref : null
  );
  if (quoteRef) {
    content.quoteRef = quoteRef;
  }

  const requestId =
    'requestId' in raw && typeof raw.requestId === 'string'
      ? raw.requestId
      : 'request_id' in raw && typeof raw.request_id === 'string'
        ? raw.request_id
        : undefined;
  if (requestId) {
    content.requestId = requestId;
  }

  const requestType =
    'requestType' in raw && typeof raw.requestType === 'string'
      ? raw.requestType
      : 'request_type' in raw && typeof raw.request_type === 'string'
        ? raw.request_type
        : undefined;
  if (requestType) {
    content.requestType = requestType;
  }

  const status =
    'status' in raw && typeof raw.status === 'string' ? raw.status : undefined;
  if (status) {
    content.status = status;
  }

  const triggerClientInstanceId =
    'triggerClientInstanceId' in raw &&
    typeof raw.triggerClientInstanceId === 'string'
      ? raw.triggerClientInstanceId
      : 'trigger_client_instance_id' in raw &&
          typeof raw.trigger_client_instance_id === 'string'
        ? raw.trigger_client_instance_id
        : undefined;
  if (triggerClientInstanceId) {
    content.triggerClientInstanceId = triggerClientInstanceId;
  }
  const record = raw as Record<string, unknown>;
  const copyStringField = (
    contentKey:
      | 'authorizationURL'
      | 'loginURL'
      | 'provider'
      | 'alias'
      | 'reason'
      | 'startURL'
      | 'description'
      | 'environmentId'
      | 'environmentLabel'
      | 'siteName'
      | 'entrypointPath'
      | 'verifiedStatus',
    ...rawKeys: string[]
  ) => {
    const value = rawKeys
      .map(key => record[key])
      .find(candidate => typeof candidate === 'string');
    if (typeof value === 'string') {
      content[contentKey] = value;
    }
  };
  copyStringField('authorizationURL', 'authorizationURL', 'authorization_url');
  copyStringField('loginURL', 'loginURL', 'login_url');
  copyStringField('provider', 'provider');
  copyStringField('alias', 'alias');
  copyStringField('reason', 'reason');
  copyStringField('startURL', 'startURL', 'start_url');
  copyStringField('description', 'description');
  copyStringField(
    'environmentId',
    'environmentId',
    'environment_id',
    'environment'
  );
  copyStringField('environmentLabel', 'environmentLabel', 'environment_label');
  copyStringField('siteName', 'siteName', 'site_name');
  copyStringField('entrypointPath', 'entrypointPath', 'entrypoint_path');
  copyStringField('verifiedStatus', 'verifiedStatus', 'verified_status');
  const hostAccess = normalizeHostAccess(
    record.hostAccess ?? record.host_access
  );
  if (hostAccess) {
    content.hostAccess = hostAccess;
  }
  const computerUseStart = normalizeComputerUseStart(
    record.computerUseStart ?? record.computer_use_start
  );
  if (computerUseStart) {
    content.computerUseStart = computerUseStart;
  }

  return content;
}

function normalizeStructuredContent(
  raw: unknown
): ConversationMessage['content'] | undefined {
  if (Array.isArray(raw)) {
    const blocks = raw.flatMap(item => {
      const normalized = normalizeContentBlock(item);
      if (!normalized) {
        return [];
      }

      if (
        normalized.type === 'text' &&
        typeof normalized.text === 'string' &&
        !normalized.quoteRef
      ) {
        return expandQuoteAwareText(normalized.text);
      }

      return [normalized];
    });
    return blocks.length > 0 ? blocks : undefined;
  }

  const normalized = normalizeContentBlock(raw);
  if (normalized) {
    if (
      normalized.type === 'text' &&
      typeof normalized.text === 'string' &&
      !normalized.quoteRef
    ) {
      return expandQuoteAwareText(normalized.text);
    }

    return [normalized];
  }

  return undefined;
}

function stringifyContentFallback(raw: unknown): string | undefined {
  if (typeof raw === 'string') {
    return raw;
  }

  try {
    return JSON.stringify(raw);
  } catch {
    return undefined;
  }
}

function parseContentBlocks(
  raw: MessageContentInput
): ConversationMessage['content'] {
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }

  if (typeof raw === 'string') {
    const parsed = parseJSON(raw);
    if (!parsed) {
      return expandQuoteAwareText(raw);
    }

    return normalizeStructuredContent(parsed) ?? expandQuoteAwareText(raw);
  }

  const normalized = normalizeStructuredContent(raw);
  if (normalized) {
    return normalized;
  }

  const fallback = stringifyContentFallback(raw);
  return fallback ? expandQuoteAwareText(fallback) : undefined;
}

function makeAssistantState(params: {
  phase: string;
  sequence: number;
  phaseStartedAt: number;
  updatedAt: number;
  reasoningText?: string;
  tools: AssistantToolCallState[];
}): AssistantState {
  return {
    phase: params.phase,
    sequence: params.sequence,
    phaseStartedAt: params.phaseStartedAt,
    updatedAt: params.updatedAt,
    reasoning: params.reasoningText?.trim()
      ? {
          messageId: undefined,
          responseId: undefined,
          text: params.reasoningText,
          isStreaming: false,
        }
      : undefined,
    messaging: undefined,
    tools: params.tools,
    asyncToolcalls: [],
  };
}

function makeConversationMessage(params: {
  id: string;
  role: string;
  timestamp: number;
  content?: MessageContentInput;
  toolUseId?: string;
  kind?: string;
  metadata?: unknown;
}): ConversationMessage {
  const message = {
    id: params.id,
    type: 'message',
    role: params.role,
    timestamp: params.timestamp,
    content: parseContentBlocks(params.content),
    toolUseId: params.toolUseId,
  } as ConversationMessage & { kind?: string; metadata?: unknown };
  if (params.kind !== undefined) {
    message.kind = params.kind;
  }
  if (params.metadata !== undefined) {
    message.metadata = params.metadata;
  }
  return message;
}

function stringField(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey = camelKey
) {
  const camelValue = raw[camelKey];
  if (typeof camelValue === 'string') {
    return camelValue;
  }
  const snakeValue = raw[snakeKey];
  return typeof snakeValue === 'string' ? snakeValue : undefined;
}

function numberField(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey = camelKey
) {
  const camelValue = raw[camelKey];
  if (typeof camelValue === 'number') {
    return camelValue;
  }
  const snakeValue = raw[snakeKey];
  return typeof snakeValue === 'number' ? snakeValue : undefined;
}

function isMessageIdValue(raw: unknown): raw is number | string {
  return typeof raw === 'number' || typeof raw === 'string';
}

const maxMessageEnvelopeDepth = 4;

function normalizeMessagePayload(
  raw: unknown,
  depth = 0
): Record<string, unknown> | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (depth > maxMessageEnvelopeDepth) {
    return null;
  }

  const hasMessageId =
    isMessageIdValue(raw.message_id) || isMessageIdValue(raw.messageId);
  if (!hasMessageId && isRecord(raw.payload)) {
    return normalizeMessagePayload(raw.payload, depth + 1);
  }

  if (isRecord(raw.message)) {
    return {
      ...raw,
      ...raw.message,
    };
  }

  return raw;
}

function normalizeMessageTimestamp(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    const parsed = Date.parse(raw);
    if (Number.isFinite(parsed)) {
      return parsed / 1000;
    }
  }
  return nowInSeconds();
}

function normalizeMessageRole(payload: Record<string, unknown>): string {
  const explicitRole = stringField(payload, 'role')?.toLowerCase();
  if (
    explicitRole === 'user' ||
    explicitRole === 'assistant' ||
    explicitRole === 'system' ||
    explicitRole === 'tool'
  ) {
    return explicitRole;
  }

  const roleLabel = stringField(payload, 'roleLabel', 'role_label')
    ?.trim()
    .toLowerCase();
  const actorType = stringField(payload, 'actorType', 'actor_type')
    ?.trim()
    .toLowerCase();
  if (actorType === 'user') {
    return 'user';
  }
  if (actorType === 'system') {
    return 'system';
  }
  if (actorType === 'tool') {
    return 'tool';
  }
  if (!actorType && roleLabel === 'user') return 'user';
  if (!actorType && roleLabel === 'system') return 'system';
  if (!actorType && roleLabel === 'tool') return 'tool';
  return 'assistant';
}

function normalizeMessageEvent(raw: unknown) {
  const payload = normalizeMessagePayload(raw);
  if (!payload) {
    return null;
  }

  const messageId =
    isMessageIdValue(payload.message_id) || isMessageIdValue(payload.messageId)
      ? ((payload.message_id ?? payload.messageId) as number | string)
      : undefined;
  if (messageId === undefined) {
    return null;
  }

  return {
    messageId,
    kind: stringField(payload, 'kind'),
    role: normalizeMessageRole(payload),
    content: payload.content,
    metadata: payload.metadata,
    toolUseId: stringField(payload, 'toolUseId', 'tool_use_id'),
    callId: stringField(payload, 'callId', 'call_id'),
    createdAt: normalizeMessageTimestamp(
      payload.created_at ?? payload.createdAt
    ),
  };
}

function isMessageUpdatedEnvelope(raw: unknown): boolean {
  return isRecord(raw) && raw.type === 'message_updated';
}

function normalizeActivity(raw: unknown): AssistantActivity | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const envelope = raw as { activity?: unknown };
  const activityRaw =
    typeof envelope.activity === 'object' && envelope.activity !== null
      ? envelope.activity
      : raw;
  if (typeof activityRaw !== 'object' || activityRaw === null) {
    return null;
  }

  const activity = activityRaw as Record<string, unknown>;
  const phase = stringField(activity, 'phase')?.trim();
  if (!phase) {
    return null;
  }

  return {
    agentId: stringField(activity, 'agentId', 'agent_id'),
    sessionId: stringField(activity, 'sessionId', 'session_id'),
    conversationId: stringField(activity, 'conversationId', 'conversation_id'),
    goal: stringField(activity, 'goal'),
    action: stringField(activity, 'action'),
    displayStrength: stringField(
      activity,
      'displayStrength',
      'display_strength'
    ),
    displayPriority: stringField(
      activity,
      'displayPriority',
      'display_priority'
    ),
    displayHoldMs: numberField(activity, 'displayHoldMs', 'display_hold_ms'),
    phase,
    summary: stringField(activity, 'summary'),
    status: stringField(activity, 'status'),
    toolCallId: stringField(activity, 'toolCallId', 'tool_call_id'),
    toolName: stringField(activity, 'toolName', 'tool_name'),
    sequence: numberField(activity, 'sequence') ?? 0,
    updatedAt:
      numberField(activity, 'updatedAt', 'updated_at') ?? nowInSeconds(),
  };
}

export interface InteractiveLoginPayload {
  status?: string;
  operation_id: string;
  login_id: string;
  login_url: string;
  start_url?: string;
  expires_at?: number;
  tool_call_id?: string;
  cookie_path?: string;
  cookie_count?: number;
  domains?: string[];
}

function parseStructuredJSON(raw: unknown): unknown {
  return typeof raw === 'string' ? parseJSON(raw) : raw;
}

function parseInteractiveLoginPayload(
  raw: unknown
): InteractiveLoginPayload | null {
  const parsed = parseStructuredJSON(raw);
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('login_id' in parsed) ||
    typeof (parsed as { login_id?: unknown }).login_id !== 'string' ||
    !('login_url' in parsed) ||
    typeof (parsed as { login_url?: unknown }).login_url !== 'string'
  ) {
    return null;
  }
  return parsed as InteractiveLoginPayload;
}

function parseToolCalls(content: unknown): TrackedToolCall[] {
  const parsed = parseStructuredJSON(content);
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('tool_calls' in parsed) ||
    !Array.isArray(parsed.tool_calls)
  ) {
    return [];
  }

  return parsed.tool_calls.flatMap(toolCall => {
    if (
      typeof toolCall !== 'object' ||
      toolCall === null ||
      !('id' in toolCall) ||
      typeof toolCall.id !== 'string' ||
      !('function' in toolCall) ||
      typeof toolCall.function !== 'object' ||
      toolCall.function === null ||
      !('name' in toolCall.function) ||
      typeof toolCall.function.name !== 'string'
    ) {
      return [];
    }

    const toolName = toolCall.function.name;
    return [
      {
        id: toolCall.id,
        name: toolName,
        kind:
          toolName === 'WebBrowse'
            ? 'web_browse'
            : toolName === 'RequestInteractiveLogin'
              ? 'interactive_login'
              : 'ignored',
      },
    ];
  });
}

export class WebChatEventAdapter {
  private accumulatedText = '';
  private reasoningSummaryText = '';
  private activeTools: AssistantToolCallState[] = [];
  private sequence = 1;
  private phase = 'idle';
  private phaseStartedAt = 0;
  private seenMessageIds = new Set<string>();
  private streamingMessageId?: string;
  private pendingToolCalls: TrackedToolCall[] = [];
  private pendingToolCallsById = new Map<string, TrackedToolCall>();
  private seenInteractiveLoginIds = new Set<string>();

  process(eventType: string, data: string): WebChatAdapterOutput {
    const output: WebChatAdapterOutput = {
      conversationMessages: [],
    };

    const parsed = parseJSON(data);

    switch (eventType) {
      case 'delta':
        this.handleDelta(parsed, output);
        break;
      case 'reasoning_summary':
        this.handleReasoningSummary(parsed, output);
        break;
      case 'tool_start':
        this.handleToolStart(parsed, output);
        break;
      case 'tool_end':
        this.handleToolEnd(parsed, output);
        break;
      case 'message':
        this.handleMessage(parsed, output, {
          replaceExisting: isMessageUpdatedEnvelope(parsed),
        });
        break;
      case 'tool_result':
        this.handleStoredToolResult(parsed, output);
        break;
      case 'message_created':
        this.handleMessage(parsed, output);
        break;
      case 'message_updated':
        this.handleMessage(parsed, output, { replaceExisting: true });
        break;
      case 'status':
        this.handleStatus(parsed, output);
        break;
      case 'activity':
        this.handleActivity(parsed, output);
        break;
      case 'title':
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          'content' in parsed &&
          typeof parsed.content === 'string'
        ) {
          output.titleChanged = parsed.content;
        }
        break;
      case 'interactive_login_requested':
        this.handleInteractiveLoginRequested(data, output);
        break;
      default:
        break;
    }

    return output;
  }

  private handleInteractiveLoginRequested(
    data: string,
    output: WebChatAdapterOutput
  ) {
    // Willow wraps custom events in a {type, payload} envelope (stream.go).
    // Unwrap so the renderer sees the login fields at the top level.
    const envelope = parseJSON(data);
    const inner =
      typeof envelope === 'object' && envelope !== null && 'payload' in envelope
        ? (envelope as { payload: unknown }).payload
        : envelope;
    if (typeof inner !== 'object' || inner === null) {
      return;
    }
    const payload = parseInteractiveLoginPayload(inner);
    if (!payload) {
      return;
    }
    if (this.seenInteractiveLoginIds.has(payload.login_id)) {
      return;
    }
    this.seenInteractiveLoginIds.add(payload.login_id);

    output.conversationMessages.push(
      makeConversationMessage({
        id: `willow-interactive-login-${payload.login_id}`,
        role: 'tool',
        timestamp: nowInSeconds(),
        content: inner,
        toolUseId: payload.tool_call_id,
      })
    );
  }

  beginRound(): WebChatAdapterOutput {
    const output: WebChatAdapterOutput = {
      conversationMessages: [],
    };
    const now = nowInSeconds();
    this.reset();
    this.enterPhase('thinking', now);
    output.assistantState = this.buildAssistantState(now);
    return output;
  }

  cancelRound(): WebChatAdapterOutput {
    const output: WebChatAdapterOutput = {
      conversationMessages: [],
    };
    if (this.phase === 'idle') {
      return output;
    }
    const now = nowInSeconds();
    this.enterPhase('idle', now);
    output.assistantState = this.buildAssistantState(now);
    this.reset();
    return output;
  }

  reset() {
    this.resetPhaseState();
    this.pendingToolCalls = [];
    this.pendingToolCallsById.clear();
    this.phase = 'idle';
  }

  private buildAssistantState(updatedAt = nowInSeconds()): AssistantState {
    return makeAssistantState({
      phase: this.phase,
      sequence: this.sequence,
      phaseStartedAt: this.phaseStartedAt,
      updatedAt,
      reasoningText:
        this.phase === 'thinking' ? this.reasoningSummaryText : undefined,
      tools: this.activeTools,
    });
  }

  private enterPhase(nextPhase: string, at = nowInSeconds()) {
    if (this.phase === nextPhase) {
      return;
    }
    this.sequence += 1;
    this.phase = nextPhase;
    this.resetPhaseState(at);
  }

  private finishRound(
    output: WebChatAdapterOutput,
    state: string,
    error?: string
  ) {
    const now = nowInSeconds();
    this.enterPhase('idle', now);
    output.assistantState = this.buildAssistantState(now);
    output.sessionFinished = error ? { state, error } : { state };
    this.reset();
  }

  private enterWait(output: WebChatAdapterOutput) {
    const now = nowInSeconds();
    if (this.phase === 'idle' || this.phase === 'thinking') {
      output.sessionStarted = true;
    }
    if (this.phase !== 'execution') {
      this.enterPhase('execution', now);
    }
    output.assistantState = this.buildAssistantState(now);
  }

  private resetPhaseState(phaseStartedAt = 0) {
    this.accumulatedText = '';
    this.reasoningSummaryText = '';
    this.streamingMessageId = undefined;
    this.activeTools = [];
    this.phaseStartedAt = phaseStartedAt;
  }

  private handleDelta(raw: unknown, output: WebChatAdapterOutput) {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('content' in raw) ||
      typeof raw.content !== 'string'
    ) {
      return;
    }
    const now = nowInSeconds();

    if (this.phase === 'idle') {
      output.sessionStarted = true;
    }
    this.enterPhase('messaging', now);

    if (!this.streamingMessageId) {
      if ('messageId' in raw && typeof raw.messageId === 'number') {
        this.streamingMessageId = `willow-${raw.messageId}`;
      } else if ('message_id' in raw && typeof raw.message_id === 'number') {
        this.streamingMessageId = `willow-${raw.message_id}`;
      } else {
        this.streamingMessageId = createPrefixedRandomID('streaming');
      }
    }

    this.accumulatedText += raw.content;
    output.conversationMessages.push(
      makeConversationMessage({
        id: this.streamingMessageId,
        role: 'assistant',
        timestamp: this.phaseStartedAt || now,
        content: this.accumulatedText,
      })
    );
    output.assistantState = this.buildAssistantState(now);
  }

  private handleReasoningSummary(raw: unknown, output: WebChatAdapterOutput) {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('content' in raw) ||
      typeof raw.content !== 'string'
    ) {
      return;
    }

    if (!raw.content) {
      return;
    }

    const now = nowInSeconds();
    if (this.phase === 'idle' || this.phase === 'thinking') {
      output.sessionStarted = true;
      this.enterPhase('thinking', now);
      this.reasoningSummaryText += raw.content;
      output.assistantState = this.buildAssistantState(now);
    }
  }

  private handleToolStart(raw: unknown, output: WebChatAdapterOutput) {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('call_id' in raw || 'callId' in raw) ||
      !('tool_name' in raw || 'toolName' in raw)
    ) {
      return;
    }

    const callId =
      ('callId' in raw && typeof raw.callId === 'string'
        ? raw.callId
        : undefined) ??
      ('call_id' in raw && typeof raw.call_id === 'string'
        ? raw.call_id
        : undefined);
    const toolName =
      ('toolName' in raw && typeof raw.toolName === 'string'
        ? raw.toolName
        : undefined) ??
      ('tool_name' in raw && typeof raw.tool_name === 'string'
        ? raw.tool_name
        : undefined);

    if (!callId || !toolName) {
      return;
    }
    const argumentsText =
      ('args' in raw && typeof raw.args === 'string' ? raw.args : undefined) ??
      ('arguments' in raw && typeof raw.arguments === 'string'
        ? raw.arguments
        : undefined);
    const summary =
      ('summary' in raw && typeof raw.summary === 'string'
        ? raw.summary
        : undefined) ??
      ('status' in raw && typeof raw.status === 'string'
        ? raw.status
        : undefined);
    const description =
      'description' in raw && typeof raw.description === 'string'
        ? raw.description
        : undefined;
    const now = nowInSeconds();

    if (this.phase === 'idle') {
      output.sessionStarted = true;
    }

    this.enterPhase('execution', now);
    this.activeTools = [
      ...this.activeTools,
      {
        callId,
        toolName,
        summary,
        description,
        args: argumentsText,
        startedAt: now,
      },
    ];
    output.assistantState = this.buildAssistantState(now);
  }

  private handleToolEnd(raw: unknown, output: WebChatAdapterOutput) {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('call_id' in raw || 'callId' in raw)
    ) {
      return;
    }

    const callId =
      ('callId' in raw && typeof raw.callId === 'string'
        ? raw.callId
        : undefined) ??
      ('call_id' in raw && typeof raw.call_id === 'string'
        ? raw.call_id
        : undefined);

    if (!callId) {
      return;
    }
    const now = nowInSeconds();

    this.activeTools = this.activeTools.map(tool =>
      tool.callId === callId
        ? {
            ...tool,
            endedAt: now,
            success: !(
              'error' in raw &&
              typeof raw.error === 'string' &&
              raw.error
            ),
            error:
              'error' in raw && typeof raw.error === 'string'
                ? raw.error
                : undefined,
          }
        : tool
    );

    output.assistantState = this.buildAssistantState(now);
  }

  private handleMessage(
    raw: unknown,
    output: WebChatAdapterOutput,
    options: { replaceExisting?: boolean } = {}
  ) {
    if (this.isStoredMessage(raw)) {
      this.handleStoredMessage(raw, output, options);
      return;
    }

    const event = normalizeMessageEvent(raw);
    if (!event) {
      return;
    }
    const messageKey = String(event.messageId);

    if (!options.replaceExisting && this.seenMessageIds.has(messageKey)) {
      return;
    }
    this.seenMessageIds.add(messageKey);

    const appRequestContent = appRequestContentFromMessageParts({
      kind: event.kind,
      metadata: event.metadata,
    });
    if (appRequestContent) {
      output.conversationMessages.push(
        makeConversationMessage({
          id: `willow-${messageKey}`,
          role: 'system',
          timestamp: event.createdAt,
          content: appRequestContent,
          kind: event.kind,
          metadata: event.metadata,
        })
      );
      return;
    }

    if (event.role === 'tool') {
      const trackedToolCall = this.resolveTrackedToolCall(
        event.toolUseId ?? event.callId
      );
      const conversationMessage = this.makeConversationMessageForToolResult({
        messageId: event.messageId,
        timestamp: event.createdAt,
        content: event.content,
        toolUseId: event.toolUseId ?? event.callId,
        trackedToolCall,
      });
      if (conversationMessage) {
        output.conversationMessages.push(conversationMessage);
      }
      return;
    }

    if (event.role === 'assistant') {
      this.registerToolCalls(event.content);
    }

    output.conversationMessages.push(
      makeConversationMessage({
        id: `willow-${messageKey}`,
        role: event.role,
        timestamp: event.createdAt,
        content: event.content,
      })
    );

    if (event.role === 'assistant') {
      this.accumulatedText = '';
      this.streamingMessageId = undefined;
      output.assistantState = this.buildAssistantState(event.createdAt);
    }
  }

  private handleStoredToolResult(raw: unknown, output: WebChatAdapterOutput) {
    if (!this.isStoredMessage(raw) || raw.role !== 'tool') {
      return;
    }

    const messageKey = String(raw.messageId);
    if (this.seenMessageIds.has(messageKey)) {
      return;
    }
    this.seenMessageIds.add(messageKey);

    const trackedToolCall = this.resolveTrackedToolCall(raw.toolUseId);
    const conversationMessage = this.makeConversationMessageForToolResult({
      messageId: raw.messageId,
      timestamp: raw.createdAt,
      content: raw.content,
      toolUseId: raw.toolUseId,
      trackedToolCall,
    });
    if (conversationMessage) {
      output.conversationMessages.push(conversationMessage);
    }
  }

  private handleStoredMessage(
    raw: WebAgentStoredMessage,
    output: WebChatAdapterOutput,
    options: { replaceExisting?: boolean } = {}
  ) {
    const messageKey = String(raw.messageId);
    if (!options.replaceExisting && this.seenMessageIds.has(messageKey)) {
      return;
    }
    this.seenMessageIds.add(messageKey);

    if (raw.role === 'tool') {
      const trackedToolCall = this.resolveTrackedToolCall(raw.toolUseId);
      const conversationMessage = this.makeConversationMessageForToolResult({
        messageId: raw.messageId,
        timestamp: raw.createdAt,
        content: raw.content,
        toolUseId: raw.toolUseId,
        trackedToolCall,
      });
      if (conversationMessage) {
        output.conversationMessages.push(conversationMessage);
      }
      return;
    }

    if (raw.role === 'assistant') {
      this.registerToolCalls(raw.content);
    }

    const appRequestContent = appRequestContentFromMessageParts({
      kind: raw.kind,
      metadata: raw.metadata,
    });
    if (appRequestContent) {
      output.conversationMessages.push(
        makeConversationMessage({
          id: `willow-${messageKey}`,
          role: 'system',
          timestamp: raw.createdAt,
          content: appRequestContent,
          kind: raw.kind,
          metadata: raw.metadata,
        })
      );
      return;
    }

    output.conversationMessages.push(
      makeConversationMessage({
        id: `willow-${messageKey}`,
        role: raw.role,
        timestamp: raw.createdAt,
        content: raw.content,
      })
    );

    if (raw.role === 'assistant') {
      this.accumulatedText = '';
      this.streamingMessageId = undefined;
      output.assistantState = this.buildAssistantState(raw.createdAt);
    }
  }

  private handleStatus(raw: unknown, output: WebChatAdapterOutput) {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('status' in raw) ||
      typeof raw.status !== 'string'
    ) {
      return;
    }

    switch (raw.status) {
      case 'paused':
      case 'completed':
        this.finishRound(output, 'completed');
        output.assistantActivity = null;
        break;
      case 'waiting':
        this.enterWait(output);
        break;
      case 'failed':
        this.finishRound(
          output,
          'failed',
          'error' in raw && typeof raw.error === 'string'
            ? raw.error
            : undefined
        );
        output.assistantActivity = null;
        break;
      case 'cancelled':
        this.finishRound(output, 'cancelled');
        output.assistantActivity = null;
        break;
      default:
        break;
    }
  }

  private handleActivity(raw: unknown, output: WebChatAdapterOutput) {
    const activity = normalizeActivity(raw);
    if (!activity) {
      return;
    }

    if (!shouldRetainAssistantActivityForSurface(activity)) {
      output.assistantActivity = null;
      return;
    }

    output.sessionStarted = true;
    output.assistantActivity = activity;
  }

  private isStoredMessage(raw: unknown): raw is WebAgentStoredMessage {
    return (
      typeof raw === 'object' &&
      raw !== null &&
      'messageId' in raw &&
      typeof raw.messageId === 'number' &&
      'role' in raw &&
      typeof raw.role === 'string' &&
      'content' in raw &&
      typeof raw.content === 'string'
    );
  }

  private registerToolCalls(content: MessageContentInput): TrackedToolCall[] {
    const trackedToolCalls = parseToolCalls(content);
    for (const toolCall of trackedToolCalls) {
      this.pendingToolCalls.push(toolCall);
      this.pendingToolCallsById.set(toolCall.id, toolCall);
    }
    return trackedToolCalls;
  }

  private resolveTrackedToolCall(
    explicitId?: string
  ): TrackedToolCall | undefined {
    if (explicitId) {
      const trackedToolCall = this.pendingToolCallsById.get(explicitId);
      if (!trackedToolCall) {
        return undefined;
      }
      this.pendingToolCallsById.delete(explicitId);
      this.pendingToolCalls = this.pendingToolCalls.filter(
        pending => pending.id !== explicitId
      );
      return trackedToolCall;
    }

    const trackedToolCall = this.pendingToolCalls.shift();
    if (!trackedToolCall) {
      return undefined;
    }
    this.pendingToolCallsById.delete(trackedToolCall.id);
    return trackedToolCall;
  }

  private makeConversationMessageForToolResult(params: {
    messageId: number | string;
    timestamp: number;
    content: MessageContentInput;
    toolUseId?: string;
    trackedToolCall?: TrackedToolCall;
  }): ConversationMessage | null {
    if (!params.trackedToolCall) {
      return null;
    }

    switch (params.trackedToolCall.name) {
      case 'WebBrowse':
        return makeConversationMessage({
          id: `willow-${params.messageId}`,
          role: 'tool',
          timestamp: params.timestamp,
          content: params.content,
          toolUseId: params.trackedToolCall.id,
        });
      case 'RequestInteractiveLogin': {
        const payload = parseInteractiveLoginPayload(params.content);
        if (payload?.login_id) {
          this.seenInteractiveLoginIds.add(payload.login_id);
        }
        return makeConversationMessage({
          id: `willow-${params.messageId}`,
          role: 'tool',
          timestamp: params.timestamp,
          content: params.content,
          toolUseId: params.trackedToolCall.id,
        });
      }
      default:
        return null;
    }
  }
}
